// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { stringify } from 'query-string';
import { useForm } from 'react-hook-form';
import { omit } from 'lodash';
import { array, arrayOf, func, object } from 'prop-types';

import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import useScrollLock from 'Hooks/useScrollLock';
import FilterSerialNumbersComponent from 'Components/Service/FilterSerialNumbers';
import useAnalytics from 'Hooks/useAnalytics';

const FilterSerialNumbers = memo(({
  manufacturers,
  sortOptions,
  currentSort,
  handleMobileOrderChange,
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const { push, location, query } = useRouter();
  const { data: { email, loggedIn } } = useUser();
  const analytics = useAnalytics();
  const {
    register,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm();

  const dateStart = watch('date_start');
  const dateEnd = watch('date_end');
  const selectedManufacturers = watch('manufacturer_name') || [];

  useEffect(() => {
    // check if query has any filters and set them in the form
    if (Object.keys(omit(query || {}, ['order_by', 'order_direction', 'page'])).length) {
      Object.entries(query).forEach(([key, value]) => {
        if (key === 'brand[]') {
          setValue('manufacturer_name', manufacturers.filter(m => {
            if (Array.isArray(value)) {
              return value.includes(String(m.id));
            }
            return String(m.id) === String(value);
          }));
          return;
        }
        setValue(key, value);
      });
    }
  }, [email, loggedIn]);

  useScrollLock(mobileFiltersOpen);

  const handleDateChange = (key, date) => {
    const selectedDate = Array.isArray(date) ? date[0] : date;
    const originalDate = new Date(selectedDate);

    if (!selectedDate || Number.isNaN(originalDate.getTime())) {
      setValue(key, '');
      return;
    }

    let day = originalDate.getDate();
    let month = originalDate.getMonth() + 1;
    const year = originalDate.getFullYear();

    // format day and month to have leading zeros if needed
    day = (day < 10) ? `0${ day }` : day;
    month = (month < 10) ? `0${ month }` : month;

    const formattedDate = `${ year }-${ month }-${ day }`;

    setValue(key, formattedDate);
  };

  const handleSubmit = () => {
    setMobileFiltersOpen(false);

    const formValues = Object.entries(getValues()).reduce((acc, [key, value]) => {
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});

    const queryParts = [];

    Object.entries(formValues)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value && value !== 'false')
      .forEach(([key, value]) => {
        if (key === 'manufacturer_name' && Array.isArray(value) && value.length > 0) {
          value.forEach(item => {
            const id = item.value || item.id || item;
            queryParts.push(`brand[]=${encodeURIComponent(id)}`);
          });
        } else {
          queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });

    const queryString = queryParts.join('&');

    const searchedValues = Object.keys(formValues).filter(k => formValues[k]).join(', ');
    analytics.track('filter_serial_numbers', { filters: searchedValues });

    push({
      location: location.pathname,
      search: `?${queryString}`
    });
  };

  const handleClearFilters = () => {
    analytics.track('clear_filters');

    setMobileFiltersOpen(false);
    reset({
      serial_number: '',
      product_code: '',
      manufacturer_name: '',
      invoice_number: '',
      date_start: '',
      date_end: '',
    });

    push({
      location: location.pathname,
      search: ''
    });
  };

  const onMobileOrderChange = sort => {
    analytics.track('mobile_sort');
    setMobileSortOpen(false);
    handleMobileOrderChange(sort);
  };

  const handleClearFilter = key => {
    analytics.track('filters_clear');
    if (key === 'brand[]') {
      setValue('manufacturer_name', '');
    } else {
      setValue(key, '');
    }
    push({
      location: location.pathname,
      search: stringify(omit(query, [key]))
    });
  };

  return (
    <FilterSerialNumbersComponent
      manufacturers={ manufacturers }
      register={ register }
      setValue={ setValue }
      onSubmit={ handleSubmit }
      onClearFilters={ handleClearFilters }
      onClearFilter={ handleClearFilter }
      activeFilters={ omit(query || {}, ['order_by', 'order_direction', 'page']) }
      mobileFiltersOpen={ mobileFiltersOpen }
      setMobileFiltersOpen={ setMobileFiltersOpen }
      mobileSortOpen={ mobileSortOpen }
      setMobileSortOpen={ setMobileSortOpen }
      sortOptions={ sortOptions }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
      dateStart={ dateStart }
      dateEnd={ dateEnd }
      onChangeDate={ handleDateChange }
      selectedManufacturers={ selectedManufacturers }
    />
  );
});

FilterSerialNumbers.propTypes = {
  manufacturers: arrayOf(object),
  sortOptions: array,
  currentSort: object,
  handleMobileOrderChange: func,
};

FilterSerialNumbers.displayName = 'FilterSerialNumbersContainer';

export default FilterSerialNumbers;
